import { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import TabContent from "./TabContent";
import { translations, languages } from "../locales/translations";

import logoEn from "../assets/logo-en.svg";
import logoJa from "../assets/logo-ja.svg";
import logoKo from "../assets/logo-ko.svg";
import logoZh from "../assets/logo-zh.svg";

const logos = {
  en: logoEn,
  ja: logoJa,
  ko: logoKo,
  zh: logoZh,
};

const NavWrapper = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
`;

const MobileNavContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MobileTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
`;

const Logo = styled.img`
  width: ${(props) => (props.$isMobile ? "160px" : "256px")};
  height: auto;
  object-fit: contain;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const MobileControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LanguageDropdown = styled.div`
  position: relative;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  color: white;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: white;
    transform: scaleX(${(props) => (props.$isActive ? 1 : 0)});
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 0.5rem;
  border-radius: 0.375rem;
  background-color: #0a0a0a;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  min-width: 120px;
  z-index: 120;
`;

const DropdownItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 1rem;
  color: rgba(230, 230, 230, 0.95);
  background: none;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

const MenuButton = styled.button`
  color: white;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileMenuPanel = styled.div`
  overflow: hidden;
  max-height: ${(p) => (p.$open ? "min(72vh, 560px)" : "0")};
  transition: max-height 0.32s ease;
  background: rgba(10, 10, 10, 0.94);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  pointer-events: ${(p) => (p.$open ? "auto" : "none")};
`;

const MobileMenuNav = styled.nav`
  max-height: min(72vh, 560px);
  overflow-y: auto;
  padding: 0.35rem 0 calc(0.75rem + env(safe-area-inset-bottom));
`;

const MobileMenuLink = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.8rem 1.15rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${(p) => (p.$active ? "#ffffff" : "rgba(255, 255, 255, 0.75)")};
  background: ${(p) => (p.$active ? "rgba(255, 255, 255, 0.07)" : "none")};
  border: none;
  border-left: 3px solid ${(p) => (p.$active ? "#ffffff" : "transparent")};
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const DesktopContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100vw;
  margin: 0 auto;
  padding: 1rem 2rem;
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 4rem;
`;

const DesktopTabList = styled.div`
  display: flex;
  gap: 1rem;
`;

const DesktopTab = styled.button`
  position: relative;
  padding: 1rem 3rem;
  color: ${(props) =>
    props.$isActive ? "white" : "rgba(255, 255, 255, 0.7)"};
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.3s ease;
  font-size: 1.1rem;
  text-transform: uppercase;
  font-weight: 500;

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: white;
    transform: scaleX(${(props) => (props.$isActive ? 1 : 0)});
    transition: transform 0.3s ease;
  }

  &:hover {
    color: white;
  }
`;

const DesktopLanguageControls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: ${(props) => (props.$isActive ? "400px" : "0")};
  transition: min-height 0.3s ease;
`;

const tabs = [
  "company",
  "product",
  "technology",
  "team",
  "partners",
  "contact",
];

function MobileNavigation({
  lang,
  activeTab,
  isMenuOpen,
  setIsMenuOpen,
  isLangMenuOpen,
  setIsLangMenuOpen,
  goHome,
  goTab,
  switchLang,
}) {
  return (
    <NavWrapper data-mobile-nav>
      <MobileNavContainer>
        <MobileTopBar>
          <Logo
            src={logos[lang]}
            alt="Logo"
            $isMobile={true}
            onClick={goHome}
          />
          <MobileControls>
            <LanguageDropdown className="language-dropdown">
              <LanguageButton
                type="button"
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              >
                {lang.toUpperCase()}
                <ChevronDown size={16} />
              </LanguageButton>
              {isLangMenuOpen && (
                <DropdownMenu>
                  {languages.map((l) => (
                    <DropdownItem
                      key={l.code}
                      type="button"
                      onClick={() => switchLang(l.code)}
                    >
                      {l.name}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              )}
            </LanguageDropdown>
            <MenuButton
              type="button"
              onClick={() => setIsMenuOpen((o) => !o)}
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </MenuButton>
          </MobileControls>
        </MobileTopBar>
        <MobileMenuPanel $open={isMenuOpen} aria-hidden={!isMenuOpen}>
          <MobileMenuNav>
            {tabs.map((tab) => (
              <MobileMenuLink
                key={tab}
                type="button"
                $active={activeTab === tab}
                onClick={() => goTab(tab)}
              >
                {translations[lang][tab]}
              </MobileMenuLink>
            ))}
          </MobileMenuNav>
        </MobileMenuPanel>
      </MobileNavContainer>
    </NavWrapper>
  );
}

function DesktopNavigation({ lang, activeTab, goHome, goTab, switchLang }) {
  return (
    <NavWrapper>
      <DesktopContainer>
        <DesktopNav>
          <Logo
            src={logos[lang]}
            alt="Logo"
            $isMobile={false}
            onClick={goHome}
          />
          <DesktopTabList>
            {tabs.map((tab) => (
              <DesktopTab
                key={tab}
                type="button"
                $isActive={activeTab === tab}
                onClick={() => goTab(tab)}
              >
                {translations[lang][tab]}
              </DesktopTab>
            ))}
          </DesktopTabList>
        </DesktopNav>
        <DesktopLanguageControls>
          {languages.map((l) => (
            <LanguageButton
              key={l.code}
              type="button"
              onClick={() => switchLang(l.code)}
              $isActive={lang === l.code}
            >
              {l.name}
            </LanguageButton>
          ))}
        </DesktopLanguageControls>
      </DesktopContainer>
    </NavWrapper>
  );
}

const Navigation = ({ lang, activeTab, onActiveTabChange }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    onActiveTabChange && onActiveTabChange(activeTab);
  }, [activeTab, onActiveTabChange]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1650);
      if (window.innerWidth >= 1650) {
        setIsMenuOpen(false);
        setIsLangMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLangMenuOpen && !event.target.closest(".language-dropdown")) {
        setIsLangMenuOpen(false);
      }
      if (
        isMenuOpen &&
        !event.target.closest("[data-mobile-nav]")
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLangMenuOpen, isMenuOpen]);

  const goHome = () => {
    if (isMobile) {
      setIsMenuOpen(false);
    }
    navigate(`/${lang}`);
  };

  const goTab = (tab) => {
    if (activeTab === tab) {
      goHome();
    } else {
      navigate(`/${lang}/${tab}`);
    }
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  const switchLang = (code) => {
    navigate(`/${code}${activeTab ? `/${activeTab}` : ""}`);
    setIsLangMenuOpen(false);
  };

  return (
    <>
      {isMobile ? (
        <MobileNavigation
          lang={lang}
          activeTab={activeTab}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          isLangMenuOpen={isLangMenuOpen}
          setIsLangMenuOpen={setIsLangMenuOpen}
          goHome={goHome}
          goTab={goTab}
          switchLang={switchLang}
        />
      ) : (
        <DesktopNavigation
          lang={lang}
          activeTab={activeTab}
          goHome={goHome}
          goTab={goTab}
          switchLang={switchLang}
        />
      )}
      <ContentWrapper $isActive={activeTab != null}>
        <TabContent tab={activeTab} currentLang={lang} />
      </ContentWrapper>
    </>
  );
};

export default Navigation;
